import React from 'react';
import styled from 'styled-components';

const AboutContainer = styled.section`
  padding: 80px 2rem;
  background: ${props => props.theme.colors.backgroundSoft};
`;

const AboutContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TextContent = styled.div`
  order: 2;
  
  @media (max-width: 768px) {
    order: 1;
  }
`;

const ImageContainer = styled.div`
  order: 1;
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    order: 2;
  }
`;

const ProfileImage = styled.img`
  width: 300px;
  height: 400px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 2px solid ${props => props.theme.colors.primary};

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 250px;
    height: 300px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  color: ${props => props.theme.colors.textWarm};
  margin-bottom: 2rem;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.9;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1.8rem;
  font-weight: 300;
`;

const Highlight = styled.span`
  color: ${props => props.theme.colors.textWarm};
  font-weight: 500;
`;

const CredentialsList = styled.ul`
  list-style: none;
  margin-top: 2.5rem;
  background: ${props => props.theme.colors.backgroundLight};
  padding: 2.5rem;
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.borderLight};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
`;

const CredentialItem = styled.li`
  padding: 1.2rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
  font-size: 1.05rem;
  color: ${props => props.theme.colors.text};
  font-weight: 400;
  position: relative;
  padding-right: 1.5rem;

  &:before {
    content: "•";
    color: ${props => props.theme.colors.primary};
    font-weight: bold;
    position: absolute;
    right: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const About: React.FC = () => {
  return (
    <AboutContainer id="about" aria-labelledby="about-title">
      <AboutContent>
        <ImageContainer>
          <ProfileImage src="/lee_freund_pic_new.jpg" alt="תמונת פרופיל של לי פרוינד, פסיכולוגית חינוכית מומחית" />
        </ImageContainer>

        <TextContent>
          <SectionTitle id="about-title">אודות</SectionTitle>
          <Description>
            אני לי פרוינד, פסיכולוגית חינוכית מומחית, נשואה ואמא לשתי בנות. במהלך שנות עבודתי פגשתי ילדים, מתבגרים והורים מגיל הגן ועד התיכון – הן במסגרות החינוך הרגיל והן במסגרות החינוך המיוחד. ליוויתי משפחות סביב מגוון רחב של נושאים: קשיי שינה, חרדות, דימוי עצמי נמוך, קשיים חברתיים, קשיי ויסות רגשי והתנהגותי, וכן מצבי משבר אישיים ומשפחתיים.
          </Description>
          
          <Description>
            לצד העבודה עם ילדים, אני פועלת גם בשיתוף פעולה עם צוותים חינוכיים, כדי לייצר תמיכה כוללת לילד. <Highlight>אני מאמינה שהבסיס לכל תהליך טיפולי הוא קשר אמיתי המבוסס על אמון.</Highlight> כל ילד זקוק למקום שבו יראו אותו, יכילו את עולמו הפנימי ויאפשרו לו להיות הוא עצמו. מרחב טיפולי בטוח מאפשר לילד לגלות את כוחותיו ולבנות בהדרגה את ה"אני האמיתי" שלו.
          </Description>
          
          <Description>
            מתוך כך, אני רואה בהורים שותפים מלאים לתהליך: ההיכרות העמוקה שלכם עם ילדכם, יחד עם הכלים המקצועיים שאני מביאה, מאפשרים בניית דרך משותפת ומשמעותית. <Highlight>בגישתי אני משלבת חשיבה פסיכודינמית עם טכניקות פרקטיות מ־CBT</Highlight>, תוך התאמה לצרכים הייחודיים של כל ילד ומשפחה.
          </Description>
          
          <Description>
            השילוב הזה מאפשר גם התבוננות רגשית מעמיקה וגם רכישת כלים יומיומיים להתמודדות. חשוב לי לייצר מרחב טיפולי אמפתי שיאפשר לילדים ולהוריהם להרגיש מקום בטוח, מכיל ותומך, וביחד להניע תהליכים משמעותיים.
          </Description>
          
          <CredentialsList>
            <CredentialItem>רישיון מומחה בפסיכולוגיה חינוכית מטעם משרד הבריאות</CredentialItem>
            <CredentialItem>רישיון עיסוק בפסיכולוגיה ורישום בפנקס הפסיכולוגים הארצי של משרד הבריאות (מספר רישיון - 27-165905)</CredentialItem>
            <CredentialItem>תואר שני (MA) בפסיכולוגיה חינוכית, אוניברסיטת בן גוריון</CredentialItem>
            <CredentialItem>תואר ראשון (BA) בפסיכולוגיה וניהול, אוניברסיטת בן גוריון</CredentialItem>
            <CredentialItem>הכשרות: טיפול בגיל הרך, טיפול בהורות, מיומנויות חברתיות, הפרעות קשב, דיאגנוסטיקה – ועוד</CredentialItem>
          </CredentialsList>
        </TextContent>
      </AboutContent>
    </AboutContainer>
  );
};

export default About;